import { isSitePageTextImageSection } from '../../../graphql/pimcore/modules/resourceLibrary/guards/resourceLibrarySitePageItems';
import {
    isInternalLinkBlock,
    isSitePageContentBlock,
    isSitePageSectionBlock,
    ResourceLibrarySitePageBlock,
    ResourceLibrarySitePageImageBlock,
    ResourceLibrarySitePageInternalLinkBlock,
    ResourceLibrarySitePageRichTableBlock,
    ResourceLibrarySitePageTableBlock,
    ResourceLibrarySitePageVideoBlock,
    ResourceLibrarySitePageLink,
} from '../../../graphql/pimcore/modules/resourceLibrary/types/resourceLibrarySitePageItems';

export interface ProcessedImage {
    url: string | null;
    alt: string | null;
    title: string | null;
    caption: string | null;
    position: 'left' | 'right';
    link: ResourceLibrarySitePageLink | null;
}

export interface ProcessedSection {
    id: string;
    level: number;
    title: string | null;
    kind: ResourceLibrarySitePageBlock['kind'];
    mergeWithPrevious: boolean;
    background: 'gray' | 'white';
    sanitizedRichText?: string;
    plainTextParagraphs?: string[];
    image?: ProcessedImage;
    hasTextContent: boolean;
    links?: ProcessedInternalLink[];
    videoBlock?: ResourceLibrarySitePageVideoBlock;
    tableBlock?: ResourceLibrarySitePageTableBlock;
    richTableBlock?: ResourceLibrarySitePageRichTableBlock;
    imageBlocks?: ResourceLibrarySitePageImageBlock[];
}

export interface ProcessedInternalLink {
    text: string;
    href: string;
    openInNewTab: boolean;
}

export function processSections(sections: ResourceLibrarySitePageBlock[]): ProcessedSection[] {
    const processed: ProcessedSection[] = [];
    const anchorStack: ProcessedSection[] = [];
    let backgroundIndex: 0 | 1 = 0;
    let initializedBackground = false;
    let nextImagePosition: 'left' | 'right' = 'right';

    sections.forEach((section, index) => {
        const mergeWithPrevious = isSitePageSectionBlock(section)
            ? section.mergeWithPrevious
            : true;

        if (!initializedBackground) {
            initializedBackground = true;
            backgroundIndex = 0;
        } else if (!mergeWithPrevious) {
            backgroundIndex = backgroundIndex === 0 ? 1 : 0;
        }

        if (isSitePageContentBlock(section)) {
            addBlockToProcessed(processed, section, index);
            return;
        }

        const previousSection = processed.length > 0 ? processed[processed.length - 1] : undefined;
        const record: ProcessedSection = {
            id: `section-${index}`,
            level: section.level,
            title: section.title ?? null,
            kind: section.kind,
            mergeWithPrevious: section.mergeWithPrevious,
            background:
                section.mergeWithPrevious && previousSection
                    ? previousSection.background
                    : backgroundIndex === 0
                      ? 'gray'
                      : 'white',
            hasTextContent: false,
        };

        switch (section.kind) {
            case 'plainTextSection':
                record.plainTextParagraphs = section.contentParagraphs;
                break;
            case 'richTextSection':
                record.sanitizedRichText = section.sanitizedContent;
                break;
            case 'plainTextImageSection':
                record.plainTextParagraphs = section.contentParagraphs;
                break;
            case 'richTextImageSection':
                record.sanitizedRichText = section.sanitizedContent;
                break;
            case 'headingSection':
                break;
            default:
                break;
        }

        const hasPlainTextContent = Boolean(
            record.plainTextParagraphs?.some((paragraph) => paragraph.trim().length > 0)
        );
        const hasRichTextContent = Boolean(
            record.sanitizedRichText && record.sanitizedRichText.trim().length > 0
        );

        record.hasTextContent = hasPlainTextContent || hasRichTextContent;

        while (anchorStack.length > 0) {
            const last = anchorStack[anchorStack.length - 1]!;
            const isParentLevel = last.level < record.level;
            const canStayAtSameLevel = record.mergeWithPrevious && last.level === record.level;
            if (isParentLevel || canStayAtSameLevel) {
                break;
            }
            anchorStack.pop();
        }
        anchorStack.push(record);

        if (isSitePageTextImageSection(section) && section.imageUrl) {
            const candidate = anchorStack.find((candidateSection) => {
                if (candidateSection === record) {
                    return false;
                }
                if (candidateSection.hasTextContent) {
                    return false;
                }
                return !candidateSection.image;
            });

            const recipient = candidate ?? record;

            if (!recipient.image) {
                recipient.image = {
                    url: section.imageUrl,
                    alt: section.imageAltText ?? null,
                    title: section.imageTitle ?? null,
                    caption: section.imageCaption ?? null,
                    position: nextImagePosition,
                    link: section.imageLink ?? null,
                };
                nextImagePosition = nextImagePosition === 'right' ? 'left' : 'right';
            }
        }

        if (record.hasTextContent) {
            anchorStack.length = 0;
        }

        const hasVisibleContent =
            (record.title && record.title.trim().length > 0) ||
            (record.plainTextParagraphs && record.plainTextParagraphs.length > 0) ||
            (record.sanitizedRichText && record.sanitizedRichText.trim().length > 0) ||
            (record.links && record.links.length > 0);

        if (hasVisibleContent) {
            processed.push(record);
        }
    });

    return processed;
}

function addBlockToProcessed(
    processed: ProcessedSection[],
    block: ResourceLibrarySitePageBlock,
    index: number
) {
    const previousSection = processed.length > 0 ? processed[processed.length - 1] : undefined;
    // Maintain the same background as the previous section when merging, otherwise default to gray
    // which is the background for the first section (however a block should never be the first
    // section but we don't perform validation here so just in case).
    const background = previousSection ? previousSection.background : 'gray';

    if (isInternalLinkBlock(block)) {
        const linkRecord = createProcessedInternalLink(block);
        if (!linkRecord) {
            return;
        }

        // If the previous section is also an internal link block, merge this link into it
        if (
            previousSection &&
            previousSection.kind === 'internalLinkBlock' &&
            previousSection.links
        ) {
            previousSection.links.push(linkRecord);
            return;
        }

        const record: ProcessedSection = {
            id: `section-${index}`,
            level: previousSection ? previousSection.level : 2,
            title: null,
            kind: 'internalLinkBlock',
            mergeWithPrevious: true,
            background,
            hasTextContent: false,
            links: [linkRecord],
        };

        processed.push(record);
        return;
    }

    if (block.kind === 'videoBlock') {
        const record: ProcessedSection = {
            id: `section-${index}`,
            level: previousSection ? previousSection.level : 2,
            title: null,
            kind: 'videoBlock',
            mergeWithPrevious: true,
            background,
            hasTextContent: false,
            videoBlock: block,
        };

        processed.push(record);
        return;
    }

    if (block.kind === 'tableBlock') {
        const record: ProcessedSection = {
            id: `section-${index}`,
            level: previousSection ? previousSection.level : 2,
            title: null,
            kind: 'tableBlock',
            mergeWithPrevious: true,
            background,
            hasTextContent: false,
            tableBlock: block,
        };

        processed.push(record);
        return;
    }

    if (block.kind === 'richTableBlock') {
        const record: ProcessedSection = {
            id: `section-${index}`,
            level: previousSection ? previousSection.level : 2,
            title: null,
            kind: 'richTableBlock',
            mergeWithPrevious: true,
            background,
            hasTextContent: false,
            richTableBlock: block,
        };

        processed.push(record);
        return;
    }

    if (block.kind === 'imageBlock') {
        if (
            previousSection &&
            previousSection.kind === 'imageBlock' &&
            previousSection.imageBlocks
        ) {
            previousSection.imageBlocks.push(block);
            return;
        }

        const record: ProcessedSection = {
            id: `section-${index}`,
            level: previousSection ? previousSection.level : 2,
            title: null,
            kind: 'imageBlock',
            mergeWithPrevious: true,
            background,
            hasTextContent: false,
            imageBlocks: [block],
        };

        processed.push(record);
        return;
    }
}

function createProcessedInternalLink(
    block: ResourceLibrarySitePageInternalLinkBlock
): ProcessedInternalLink | null {
    const text = block.linkText?.trim() ?? '';
    const href = block.linkRoute?.trim() ?? '';

    if (!text || !href) {
        return null;
    }

    return {
        text,
        href,
        openInNewTab: block.openInNewTab,
    };
}
